import { useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { getLoggedInUser } from '#helpers/authUtils';
import { authProtectedRoutes } from '#routes';
import {
    buildNavigationMenu,
    filterMenuByRole,
    getActivatedMenuItemIds,
    toggleExpandedItem,
} from '../../../../../../services/navigation/navigation-menu.service';

const navigationMenu = buildNavigationMenu(authProtectedRoutes);

export default function useAppMenuController({ onNavigate } = {}) {
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useSelector((state) => state.Auth);
    const loggedInUser = getLoggedInUser();

    const [expandedItems, setExpandedItems] = useState([]);

    const menuItems = useMemo(() => {
        if (!loggedInUser || !user) return [];
        return filterMenuByRole(navigationMenu, loggedInUser.role);
    }, [loggedInUser, user]);

    const activatedMenuItemIds = useMemo(
        () => getActivatedMenuItemIds(menuItems, location.pathname),
        [menuItems, location.pathname]
    );

    const isItemActive = useCallback(
        (item) => activatedMenuItemIds.includes(item.id),
        [activatedMenuItemIds]
    );

    const isItemExpanded = useCallback(
        (item) => expandedItems.includes(item.id),
        [expandedItems]
    );

    const toggleItem = useCallback((itemId) => {
        setExpandedItems((prev) => toggleExpandedItem(prev, itemId));
    }, []);

    const handleLeafClick = useCallback(
        (item) => {
            if (item?.path) {
                navigate(item.path);
            }
            onNavigate?.();
        },
        [navigate, onNavigate]
    );

    return {
        isAuthenticated: Boolean(loggedInUser && user),
        menuItems,
        isItemActive,
        isItemExpanded,
        toggleItem,
        handleLeafClick,
    };
}
